// <pfs-toast-host>: Globaler Toast-Container am Body-Ende. Hoert auf
// Custom-Events `pfs:toast` von document.body + rendert Toast-Karten
// rechts oben. Self-cleanup via timeout oder click.
//
// Zwei Quellen (PFS-133 + feedback_realtime_toast_preserve.md):
//   1. Server-Push (SSE/WebSocket → pfs-events)
//      Authentifizierter Stream nur in Authenticated-Layout.
//      Optional: data-stream-url-Attribut → Component subscribed selbst
//   2. UI-Action-Feedback
//      Andere Components dispatchen pfs:toast-events bei Form-Errors etc.
//      (z.B. <pfs-login-form> bei fehlgeschlagenem Login).
//
// Event-Shape:
//   document.body.dispatchEvent(new CustomEvent("pfs:toast", {
//     detail: {
//       title: "Login fehlgeschlagen",     // optional
//       message: "Ungueltige Zugangsdaten", // required
//       variant: "error",                   // info | success | warning | error
//       duration: 5000,                     // ms, 0 = sticky (Default 4500)
//     }
//   }));
//
// Attribute (alle optional):
//   data-stream-url    URL fuer SSE-Subscription (Server-Push-Toasts)
//   data-position      "top-right" | "top-left" | "bottom-right" | "bottom-left"
//                       (Default: "top-right")

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";

const TOAST_EVENT = "pfs:toast";

type Variant = "info" | "success" | "warning" | "error";

interface ToastDetail {
  title?: string;
  message: string;
  variant?: Variant;
  duration?: number;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  /* PFS-580: absolute statt fixed → scopt auf die positionierte pfs-shell
     (App-Root), nicht den Viewport. Im WP-Embed ankert es damit an der
     Embed-Region statt der ganzen WP-Seite. Voraussetzung: der Host sitzt IN
     der Shell-Shadow (Konsumenten-Layout) + die Shell ist position:relative. */
  position: absolute;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
}
:host([data-position="top-right"]),
:host(:not([data-position])) {
  top: 1rem;
  right: 1rem;
}
:host([data-position="top-left"]) { top: 1rem; left: 1rem; }
:host([data-position="bottom-right"]) { bottom: 1rem; right: 1rem; }
:host([data-position="bottom-left"]) { bottom: 1rem; left: 1rem; }

.toast {
  pointer-events: auto;
  min-width: 18rem;
  max-width: 24rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: var(--pfs-color-surface);
  box-shadow: var(--pfs-shadow-overlay, 0 10px 25px rgba(0,0,0,0.15));
  border-left: 4px solid var(--pfs-color-primary);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  animation: pfs-toast-in 0.2s ease-out;
}
.toast[data-variant="success"] { border-left-color: var(--pfs-color-on-success-container); }
.toast[data-variant="warning"] { border-left-color: var(--pfs-color-on-warning-container); }
.toast[data-variant="error"]   { border-left-color: var(--pfs-color-on-danger-container); }
.toast__title { font-size: 0.875rem; font-weight: 600; color: var(--pfs-color-on-surface); }
.toast__message { font-size: 0.875rem; color: var(--pfs-color-on-surface-muted); }
.toast__close {
  position: absolute; top: 0.25rem; right: 0.5rem;
  background: transparent; border: none; cursor: pointer;
  font-size: 1rem; color: var(--pfs-color-on-surface-subtle);
  padding: 0.25rem;
}
.toast__close:hover { color: var(--pfs-color-on-surface-muted); }
.toast { position: relative; padding-right: 2rem; }

@keyframes pfs-toast-in {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

`;

defineComponent({
  tagName: "pfs-toast-host",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const style = document.createElement("style");
    style.textContent = STYLES;
    ctx.shadowRoot.appendChild(style);

    const list = document.createElement("div");
    list.className = "pfs-toast-list";
    ctx.shadowRoot.appendChild(list);

    const renderToast = (detail: ToastDetail) => {
      const variant = detail.variant ?? "info";
      const duration = detail.duration ?? 4500;

      const toast = document.createElement("div");
      toast.classList.add("toast");
      toast.setAttribute("data-variant", variant);
      toast.setAttribute("role", variant === "error" ? "alert" : "status");

      if (detail.title) {
        const t = document.createElement("div");
        t.classList.add("toast__title");
        t.textContent = detail.title;
        toast.appendChild(t);
      }
      const m = document.createElement("div");
      m.classList.add("toast__message");
      m.textContent = detail.message;
      toast.appendChild(m);

      const close = document.createElement("button");
      close.classList.add("toast__close");
      close.type = "button";
      close.setAttribute("aria-label", "Schließen");
      close.textContent = "✕";
      close.addEventListener("click", () => toast.remove());
      toast.appendChild(close);

      list.appendChild(toast);
      if (duration > 0) {
        setTimeout(() => toast.remove(), duration);
      }
    };

    const onToastEvent = (event: Event) => {
      const ce = event as CustomEvent<ToastDetail>;
      if (!ce.detail || !ce.detail.message) return;
      renderToast(ce.detail);
    };

    document.body.addEventListener(TOAST_EVENT, onToastEvent);
    ctx.cleanup.push(() => document.body.removeEventListener(TOAST_EVENT, onToastEvent));

    // PFS-425 (2026-05-20): pfs:login-error Auto-Listen entfernt — der
    // ehemalige <pfs-auth-login> ROPC-WC ist obsolet, <pfs-login-form>
    // dispatcht direkt pfs:toast (variant:"error", title+message) bei
    // data-error-message. Kein Indirektions-Event mehr noetig.

    // Optionale SSE-Subscription fuer Server-Push-Toasts.
    const streamUrl = host.getAttribute("data-stream-url");
    if (streamUrl) {
      const es = new EventSource(streamUrl);
      es.addEventListener("toast", (event) => {
        try {
          const detail = JSON.parse((event as MessageEvent).data) as ToastDetail;
          renderToast(detail);
        } catch (err) {
          console.warn("[pfs-toast-host] SSE parse error", err);
        }
      });
      ctx.cleanup.push(() => es.close());
    }
  },
});
